import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Upload, MapPin, History, Trash2, Download } from "lucide-react";
import { AuditLogDialog } from "../components/AuditLogDialog";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";

// Componente para mostrar produtos em um endereço
function AddressProducts({ endereco, cbm }: { endereco: string; cbm?: number }) {
  const { data: products = [], isLoading } = trpc.addresses.products.useQuery({ endereco }) as { data: { productCod: string; descricao: string; quantidade: number; cubagem: number }[]; isLoading: boolean };
  
  if (isLoading) return <span className="text-muted-foreground text-xs">Carregando...</span>;
  if (products.length === 0) return <span className="text-green-600 text-xs font-medium">Vazio</span>;
  
  const totalCubagem = products.reduce((acc, p) => acc + p.cubagem, 0);
  const ocupacaoPct = cbm && cbm > 0 ? Math.min((totalCubagem / cbm) * 100, 100) : null;
  const barColor = ocupacaoPct === null ? 'bg-gray-300' : ocupacaoPct > 90 ? 'bg-red-500' : ocupacaoPct > 70 ? 'bg-yellow-500' : 'bg-green-500';
  
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {products.map((prod) => (
          <span key={prod.productCod} className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded" title={`${prod.descricao} | Qtd: ${parseInt(prod.quantidade)} | CBM: ${prod.cubagem.toFixed(4)}`}>
            {prod.productCod} x{parseInt(prod.quantidade)}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} rounded-full`} style={{ width: `${ocupacaoPct ?? 0}%` }}></div>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {totalCubagem.toFixed(4)}{cbm ? ` / ${Number(cbm).toFixed(4)} m³` : ' m³'}
          {ocupacaoPct !== null && ` (${ocupacaoPct.toFixed(0)}%)`}
        </span>
      </div>
    </div>
  );
}

export default function Addresses() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Estados do formulário
  const [rua, setRua] = useState("");
  const [coluna, setColuna] = useState("");
  const [andar, setAndar] = useState("");
  const [posicao, setPosicao] = useState("");
  const [tipo, setTipo] = useState<"Palete" | "Picking">("Palete");
  const [altura, setAltura] = useState("");
  const [largura, setLargura] = useState("");
  const [comprimento, setComprimento] = useState("");

  // Queries e mutations
  const { data: addresses = [], refetch } = trpc.addresses.list.useQuery(
    undefined,
    { enabled: !!user && (user.role === "lider" || user.role === "admin") }
  );
  
  // Filtrar endereços por busca
  const filteredAddresses = addresses.filter((address) => {
    const search = searchTerm.toLowerCase();
    return (
      address.endereco.toLowerCase().includes(search) ||
      address.tipo.toLowerCase().includes(search)
    );
  });
  
  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  
  // Calcular paginação
  const totalPages = Math.ceil(filteredAddresses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAddresses = filteredAddresses.slice(startIndex, endIndex);

  const createMutation = trpc.addresses.create.useMutation({
    onSuccess: () => {
      toast.success("Endereço cadastrado com sucesso");
      refetch();
      setShowForm(false);
      // Limpar campos
      setRua("");
      setColuna("");
      setAndar("");
      setPosicao("");
      setTipo("Palete");
      setAltura("");
      setLargura("");
      setComprimento("");
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao cadastrar endereço");
    },
  });

  const createBulkMutation = trpc.addresses.createBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} endereços importados com sucesso`);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao importar endereços");
    },
  });

  const deleteMutation = trpc.addresses.delete.useMutation({
    onSuccess: () => {
      toast.success("Endereço deletado");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao deletar endereço");
    },
  });
  
  const updateMutation = trpc.addresses.update.useMutation({
    onSuccess: () => {
      toast.success("Endereço atualizado com sucesso");
      setEditingId(null);
      setEditValues({});
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar endereço");
    },
  });
  
  const { data: auditLogs = [], isLoading: auditLoading } = trpc.addresses.auditLog.useQuery(
    { addressId: selectedAddressId! },
    { enabled: !!selectedAddressId && auditDialogOpen }
  );
  
  const handleStartEdit = (address: any) => {
    setEditingId(address.id);
    setEditValues({
      altura: address.altura || "",
      largura: address.largura || "",
      comprimento: address.comprimento || "",
    });
  };
  
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };
  
  const handleSaveEdit = async (id: number) => {
    await updateMutation.mutateAsync({
      id,
      altura: Number(editValues.altura),
      largura: Number(editValues.largura),
      comprimento: Number(editValues.comprimento),
    });
  };

  // Handlers
  const handleSubmit = () => {
    if (!rua || !coluna || !andar) {
      toast.error("Rua, Coluna e Andar são obrigatórios");
      return;
    }

    if (rua.length !== 2 || coluna.length !== 2 || andar.length !== 2) {
      toast.error("Rua, Coluna e Andar devem ter 2 dígitos");
      return;
    }

    if (tipo === "Palete" && (!posicao || posicao.length !== 2)) {
      toast.error("Posição é obrigatória para tipo Palete e deve ter 2 dígitos");
      return;
    }

    createMutation.mutate({
      rua,
      coluna,
      andar,
      posicao: tipo === "Palete" ? posicao : undefined,
      tipo,
      altura: altura ? parseInt(altura) : undefined,
      largura: largura ? parseInt(largura) : undefined,
      comprimento: comprimento ? parseInt(comprimento) : undefined,
    });
  };

  const handleExportExcel = () => {
    try {
      // Preparar dados para exportação (usar dados filtrados)
      const dataToExport = filteredAddresses.map((address) => ({
        "ENDEREÇO": address.endereco,
        "RUA": address.rua,
        "COLUNA": address.coluna,
        "ANDAR": address.andar,
        "POSIÇÃO": address.posicao || "",
        "TIPO": address.tipo,
        "ALTURA (CM)": address.altura || "",
        "LARGURA (CM)": address.largura || "",
        "COMPRIMENTO (CM)": address.comprimento || "",
        "CBM (m³)": address.cbm ? Number(address.cbm).toFixed(6) : "",
      }));

      // Criar workbook e worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Endereços");

      // Gerar arquivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `enderecos_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Endereços exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar endereços");
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // Dados de exemplo para o template
      const templateData = [
        {
          "Rua": "01",
          "Coluna": "01",
          "Andar": "01",
          "Posição": "01",
          "Tipo": "Palete",
          "Altura (CM)": 150,
          "Largura (CM)": 100,
          "Comprimento (CM)": 120,
        },
        {
          "Rua": "01",
          "Coluna": "02",
          "Andar": "01",
          "Posição": "",
          "Tipo": "Picking",
          "Altura (CM)": 50,
          "Largura (CM)": 60,
          "Comprimento (CM)": 40,
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "template_enderecos.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Template baixado!");
    } catch (error) {
      toast.error("Erro ao baixar template");
    }
  };
  
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json<any>(ws);

        const addressList = data.map((row: any) => ({
          rua: String(row["Rua"] || "").padStart(2, "0"),
          coluna: String(row["Coluna"] || "").padStart(2, "0"),
          andar: String(row["Andar"] || "").padStart(2, "0"),
          posicao: row["Posição"] ? String(row["Posição"]).padStart(2, "0") : undefined,
          tipo: (row["Tipo"] === "Picking" ? "Picking" : "Palete") as "Palete" | "Picking",
          altura: row["Altura (CM)"] ? Number(row["Altura (CM)"]) : undefined,
          largura: row["Largura (CM)"] ? Number(row["Largura (CM)"]) : undefined,
          comprimento: row["Comprimento (CM)"] ? Number(row["Comprimento (CM)"]) : undefined,
        }));

        createBulkMutation.mutate({ addresses: addressList });
      } catch (error) {
        toast.error("Erro ao processar arquivo Excel");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const handleDelete = (id: number) => {
    if (confirm("Tem certeza que deseja deletar este endereço?")) {
      deleteMutation.mutate({ id });
    }
  };

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Loading check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  return (
    <>
    <div className="min-h-screen bg-background p-4">
      <div className="container max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MapPin className="h-6 w-6" />
              Cadastro de Endereços
            </h1>
            <p className="text-sm text-muted-foreground">Gerencie os endereços do armazém</p>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por endereço ou tipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar Template
            </Button>
            <label htmlFor="import-addresses">
              <Button variant="outline" size="sm" asChild>
                <span className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar Excel
                </span>
              </Button>
            </label>
            <input
              id="import-addresses"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
            />
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              {showForm ? "Cancelar" : "Novo Endereço"}
            </Button>
          </div>
        </div>

        {/* Formulário de cadastro */}
        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Novo Endereço</CardTitle>
              <CardDescription>Preencha os dados do endereço</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Rua * (2 dígitos)
                  </label>
                  <Input
                    value={rua}
                    onChange={(e) => setRua(e.target.value.slice(0, 2))}
                    placeholder="01"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Coluna * (2 dígitos)
                  </label>
                  <Input
                    value={coluna}
                    onChange={(e) => setColuna(e.target.value.slice(0, 2))}
                    placeholder="05"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Andar * (2 dígitos)
                  </label>
                  <Input
                    value={andar}
                    onChange={(e) => setAndar(e.target.value.slice(0, 2))}
                    placeholder="02"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Tipo *
                  </label>
                  <Select value={tipo} onValueChange={(v) => setTipo(v as "Palete" | "Picking")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Palete">Palete</SelectItem>
                      <SelectItem value="Picking">Picking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                {tipo === "Palete" && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Posição * (2 dígitos)
                    </label>
                    <Input
                      value={posicao}
                      onChange={(e) => setPosicao(e.target.value.slice(0, 2))}
                      placeholder="03"
                      maxLength={2}
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Altura (CM)
                  </label>
                  <Input
                    type="number"
                    value={altura}
                    onChange={(e) => setAltura(e.target.value)}
                    placeholder="120"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Largura (CM)
                  </label>
                  <Input
                    type="number"
                    value={largura}
                    onChange={(e) => setLargura(e.target.value)}
                    placeholder="80"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Comprimento (CM)
                  </label>
                  <Input
                    type="number"
                    value={comprimento}
                    onChange={(e) => setComprimento(e.target.value)}
                    placeholder="100"
                  />
                </div>
              </div>

              {altura && largura && comprimento && (
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">
                    CBM Calculado:{" "}
                    {((parseInt(altura) * parseInt(largura) * parseInt(comprimento)) / 1000000).toFixed(6)} m³
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela de endereços */}
        <Card>
          <CardHeader>
            <CardTitle>Endereços Cadastrados ({filteredAddresses.length}{searchTerm ? ` de ${addresses.length}` : ""})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">
                      ID
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Endereço
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Tipo
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Dimensões (CM)
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      CBM (m³)
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Produtos
                    </th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddresses.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum endereço cadastrado
                      </td>
                    </tr>
                  ) : (
                    paginatedAddresses.map((address) => {
                      const isEditing = editingId === address.id;
                      const editCbm = isEditing && editValues.altura && editValues.largura && editValues.comprimento
                        ? ((Number(editValues.altura) * Number(editValues.largura) * Number(editValues.comprimento)) / 1000000).toFixed(6)
                        : address.cbm;
                      
                      return (
                        <tr key={address.id} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-3 text-sm text-muted-foreground">{address.id}</td>
                          <td className="py-2 px-3 text-sm font-medium">{address.endereco}</td>
                          <td className="py-2 px-3 text-sm text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                address.tipo === "Palete"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {address.tipo}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center items-center">
                                <Input
                                  type="number"
                                  value={editValues.altura}
                                  onChange={(e) => setEditValues({ ...editValues, altura: e.target.value })}
                                  className="h-7 text-xs w-16 text-center"
                                  placeholder="A"
                                />
                                <span className="text-xs">×</span>
                                <Input
                                  type="number"
                                  value={editValues.largura}
                                  onChange={(e) => setEditValues({ ...editValues, largura: e.target.value })}
                                  className="h-7 text-xs w-16 text-center"
                                  placeholder="L"
                                />
                                <span className="text-xs">×</span>
                                <Input
                                  type="number"
                                  value={editValues.comprimento}
                                  onChange={(e) => setEditValues({ ...editValues, comprimento: e.target.value })}
                                  className="h-7 text-xs w-16 text-center"
                                  placeholder="C"
                                />
                              </div>
                            ) : (
                              <span className="text-muted-foreground">
                                {address.altura && address.largura && address.comprimento
                                  ? `${address.altura} × ${address.largura} × ${address.comprimento}`
                                  : "-"}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm text-center text-muted-foreground">
                            {editCbm || "-"}
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <AddressProducts endereco={address.endereco} cbm={address.cbm ? Number(address.cbm) : undefined} />
                          </td>
                          <td className="py-2 px-3 text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSaveEdit(address.id)}
                                  disabled={updateMutation.isPending}
                                >
                                  Salvar
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartEdit(address)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAddressId(address.id);
                                    setAuditDialogOpen(true);
                                  }}
                                  title="Ver Histórico"
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(address.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Controles de paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({filteredAddresses.length} endereços)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    
    <AuditLogDialog
      open={auditDialogOpen}
      onOpenChange={setAuditDialogOpen}
      title="Histórico de Alterações do Endereço"
      logs={auditLogs}
      isLoading={auditLoading}
    />
    </>
  );
}
